<template>
  <div>
    <ParagraphWithTitle
      v-if="!showActivitesText"
      class="mb-6 md:mb-5"
      title-key="charterAllTrips.allTripsSection.title"
      text-key="charterAllTrips.allTripsSection.text"
    />
    <div
      v-if="showActivitesText"
      class="mb-6 md:mb-5"
    >
      <div
        class="font-secondary h4 font-bold"
        :data-i18n="$t('charterActivitiesAllDestinationsHeader')"
      >
        {{ $t('charterActivitiesAllDestinationsHeader') }}
      </div>
      <p :data-i18n="$t('charterActivitiesAllDestinationsSubtext')">
        {{ $t('charterActivitiesAllDestinationsSubtext') }}
      </p>
    </div>
    <SimpleAirportDropdown
      v-if="showAirportSelect"
      :selected-airport="selectedAirport"
      :airports="filterAirportsWithDestinations"
      @on-select="selectAirport"
    />
    <div class="flex flex-wrap -mx-4">
      <div
        v-for="(group, groupIndex) in airportFilteredGroupedDestinations"
        :key="groupIndex"
        class="w-12/12 md:w-4/12 px-4 flex flex-col overflow-hidden"
      >
        <RelativeDropdown
          v-for="(country, indexInGroup) in group"
          :key="`${groupIndex}-${indexInGroup}`"
          ref="gridDropdown"
          class="w-full"
          :data-test-countrydropdown-group="groupIndex"
          :data-test-countrydropdown-country="country.name"
        >
          <template #trigger="{ toggle, isOpen }">
            <button
              class="shadow-none btn normal-case !w-full pl-6 py-6 pr-2 !m-0 relative text-left flex justify-between"
              type="button"
              :class="[isOpen ? 'bg-white' : 'bg-light-turquoise', { 'md:pt-12': indexInGroup === 0, 'md:pb-12': indexInGroup === group.length - 1 }]"
              @click="toggle"
            >
              <div class="h6 grid-dropdown-text mb-0">
                {{ country.name }}
              </div>
              <div class="icon-dropdown grid-dropdown-icon pr-6 h-full flex items-center text-dark-turquoise">
                <fa :icon="isOpen ? 'chevron-down' : 'chevron-right'" />
              </div>
            </button>
          </template>
          <template #content>
            <div class="w-full bg-white-turquoise py-4">
              <nuxt-link
                v-for="destination in country.destinations"
                :key="destination.destination_id"
                class="dropdown-item py-2"
                type="button"
                :to="`${localeURLs.destinations}/${destination.destination_page_id}`"
                :data-test-destination-countrydropdown-group="`${groupIndex}-${indexInGroup}`"
              >
                <div class="h6 mb-0 font-normal">
                  {{ destination.name }}
                </div>
              </nuxt-link>
            </div>
          </template>
        </RelativeDropdown>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'

export default defineNuxtComponent({
  setup () {
    //TODO: remove when package search bar is functional
    useAsyncData(async () => {
      await useCharterPackagesStore().getDestinations()
    })
    const { urls: localeURLs } = useLocale()

    return {
      localeURLs,
    }
  },

  props: {
    prefilledSelectedAirport: {
      type: Object,
      default: () => ({}),
    },

    showAirportSelect: {
      type: Boolean,
      default: true,
    },

    showActivitesText: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['selectAirport'],

  data () {
    return {
      selectedAirport: this.$props.prefilledSelectedAirport || {},
      airportFilteredGroupedDestinations: [],
    }
  },

  computed: {
    ...mapState(useRootStore, {
      destinations: 'charterDestinations',
      airports: 'charterAirports',
    }),

    ...mapState(useCharterPackagesStore, [
      'destinationIdsByAirport',
    ]),

    hasSelectedAirport () {
      return this.selectedAirport.airport_name
    },

    airportsWithoutDestinations () {
      return (this.airports || [])
        .filter(
          ({ airport }) => this.destinations.every(country => country.destinations.every(destination => !destination.avalible_from_airports.includes(airport)))
        )
        .map(({ airport }) => airport)
    },

    filterAirportsWithDestinations () {
      return (this.airports || []).filter(({ airport }) => !this.airportsWithoutDestinations.includes(airport))
    },
  },

  watch: {
    prefilledSelectedAirport () {
      this.selectAirport(this.prefilledSelectedAirport)
    },

    destinations: {
      handler () {
        this.airportFilterGroupDestinations()
      },

      immediate: true,
    },
  },

  methods: {
    airportFilterGroupDestinations () {
      const sortedWithoutEmptyCountriesDestinations = [...this.destinations]
        .filter(country => country.destinations?.length)
        .sort((a, b) => a.name > b.name ? 1 : -1)

      if (!this.hasSelectedAirport) {
        this.airportFilteredGroupedDestinations = groupArray(sortedWithoutEmptyCountriesDestinations, 3)
        return
      }

      const airportDestinationIds = this.destinationIdsByAirport(this.selectedAirport)
      const filteredDestinations = []
      sortedWithoutEmptyCountriesDestinations.forEach(
        (country) => {
          const destinationsWithSelectedAirport = country.destinations.filter(
            destination => airportDestinationIds.includes(destination.destination_id)
          )
          if (destinationsWithSelectedAirport.length) {
            filteredDestinations.push({
              ...country,
              destinations: destinationsWithSelectedAirport,
            })
          }
        })

      this.airportFilteredGroupedDestinations = groupArray(filteredDestinations, 3)
    },

    selectAirport (airport) {
      this.$refs.gridDropdown?.forEach(dropdown => dropdown?.close())
      this.selectedAirport = airport
      this.$emit('selectAirport', airport)
      this.airportFilterGroupDestinations()
    },
  },
})
</script>

<style lang="scss">
.grid {
  &-dropdown-icon {
    font-size: 1.2rem;
  }

  &-dropdown-text {
    font-size: 1.1rem;
  }
}
</style>
